import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import './chat.css';
import { BarteringDocument, BarteringTransaction, BsUserLoggedIn, ExtendedBarteringDocument, Listing, ProductTag, ReducedBarteringTransaction, TransactionType, WithStringId } from './types';
import { ListedView } from './listings';
import api from './api';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import ImageBlur from './imageblur';
interface FloatingCardProps {
    button: JSX.Element;
    top?: boolean;
    bottom?: boolean;
    width?: number;
}

const FloatingCard: FunctionComponent<FloatingCardProps> = ({
    button,
    top,
    bottom,
    width = 400,
    children,
}) => {
    const [showCard, setShowCard] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    const handleClickOutside = (event: MouseEvent) => {
        if (
            cardRef.current &&
            showCard &&
            !cardRef.current.contains(event.target as Node)
        ) {
            setShowCard(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showCard]);

    useEffect(() => {
        if (buttonRef.current && cardRef.current && showCard) {
            const buttonRect = buttonRef.current.getBoundingClientRect();
            const cardRect = cardRef.current.getBoundingClientRect();
            const halfCardWidth = width / 2;
            const halfButtonWidth = buttonRect.width / 2;

            const cardLeft = '-' + (halfCardWidth - halfButtonWidth);

            if (top) {
                cardRef.current.style.bottom = buttonRect.height + `px`;
            } else if (bottom) {
                cardRef.current.style.top = buttonRect.height + `px`;
            }

            cardRef.current.style.left = `${cardLeft}px`;
        }
    }, [showCard, top, bottom, width]);

    const buttonWithOnClick = React.cloneElement(button, {
        onClick: (e: React.MouseEvent) => {
            setShowCard(!showCard);
            if (button.props.onClick) {
                button.props.onClick(e);
            }
        },
    });


    return (
        <div className="position-relative">
            <div
                className={classNames(showCard ? '' : 'd-none')}
                style={{ width, position: 'absolute', zIndex: 1005 }}
                ref={cardRef}
            >
                <div className="card">
                    <div className="card-body">{children}</div>
                </div>
            </div>
            <div ref={buttonRef}>{buttonWithOnClick}</div>
        </div>
    );
};

export const TradeCenter: FunctionComponent<{
    user: WithStringId<BsUserLoggedIn>,
    tags: Array<ProductTag>,
    refreshParentBarters: () => void
}> = ({ user, tags, refreshParentBarters }) => {
    const [currentItem, setCurrentItem] = useState('');
    const [ready, setReady] = useState(false);
    const [error, setError] = useState('');
    const [index, setIndex] = useState(-1);
    const [barters, setBarters] = useState([] as Array<ExtendedBarteringDocument>);

    const submitBarter = (id: string, items?: Array<string>, price?: number) => {
        return api.counterOffer(id, items, price)
            .then(() => refreshAllBarters())
    }
    const refreshAllBarters = () => {
        setReady(false);
        refreshParentBarters();
        return api.tradecenter()
            .then(barters => {
                console.log('Trade Center:', barters)
                setBarters(barters)
            })
            .then(() => setReady(true))
            .catch(err => {
                console.error(err);
                setReady(true);
            })
    }
    useEffect(() => {
        refreshAllBarters()

    }, []);

    const agreeText = (outgoing: boolean, owner: boolean) => {
        if (outgoing && !owner) { //the buyer has agreed to the trade (buyer perspective)
            return 'I\'ve agreed to the trade'
        }
        if (outgoing && owner) { // seller has agreed to the trade (buyer perspective)
            return 'The seller has agreed to the trade'
        }
        if (!outgoing && !owner) { //buyer has agreed to the trade (seller perspective)
            return 'The buyer has agreed to the trade'
        }
        if (!outgoing && owner) { // seller has agreed to the trade (seller perspective)
            return 'I\'ve agreed to the trade';
        }
    }
    const declineText = (outgoing: boolean, owner: boolean) => {
        if (outgoing && !owner) { //the buyer has declined the trade (buyer perspective)
            return 'Trade declined'
        }
        if (outgoing && owner) { // seller has declined the trade (buyer perspective)
            return 'The seller has declined the trade'
        }
        if (!outgoing && !owner) { //buyer has declined to the trade (seller perspective)
            return 'The buyer has declined the trade'
        }
        if (!outgoing && owner) { // seller has declined to the trade (seller perspective)
            return 'Trade declined.';
        }
    }

    const compareItems = (previousItems: Array<string>, currentItems: Array<string>): boolean => {
        // Sort both arrays in alphabetical order
        previousItems.sort();
        currentItems.sort();

        if (currentItems.length !== previousItems.length) return false;

        for (let i = 0; i < previousItems.length; i++) {
            if (previousItems[i] !== currentItems[i]) {
                return false;
            }
        }
        return true;
    }

    const counterText = (outgoing:
        boolean,
        owner: boolean,
        previousItems: Array<string>,
        items: Array<string>,
        previousPrice: number,
        price: number,
        buyerInventory: Array<WithStringId<Listing>>) => {
        const productImage = (listingId: string) => buyerInventory.find(({ _id }) => _id === listingId)?.images[0];

        let itemSection;
        let priceSection;

        if ((outgoing && !owner) || (!outgoing && !owner)) { //the buyer has offered a counter offer (buyer perspective)
            if (!compareItems(previousItems, items)) {
                itemSection = <div>
                    I want to offer the following items:
                    <div className='row row-cols-1 row-cols-sm-2 row-cols-md-6 g-1'>
                        {items.map(item => <div className='col'>
                            <ImageBlur src={'/api/image?id=' + productImage(item)} />
                        </div>)}
                    </div>
                </div>
            }
            if (price !== previousPrice)
                priceSection = <div>
                    {itemSection && ('And I would like to adjust the price to $' + price) || ('I would like to buy this at a price of $' + price + ' instead.')}
                </div>

        }
        if ((outgoing && owner) || (!outgoing && owner)) { // seller has offered a counter offer (seller perspective)
            if (items.length) {
                itemSection = <div>
                    I would like to request the following items from you:
                    <div className='row row-cols-1 row-cols-sm-2 row-cols-md-6 g-1'>
                        {items.map(item => <div className='col'>
                            <ImageBlur src={'/api/image?id=' + productImage(item)} />
                        </div>)}
                    </div>
                </div>
            }
            priceSection = <div>
                {itemSection && ('And I would like to adjust the price to $' + price) || ('I would prefer an asking price of $' + price + ' instead.')}
            </div>
        }
        return <div>
            <strong>Counter offer</strong>

            {itemSection}
            {priceSection}
        </div>
    }
    const currentBarterAgreed = index !== -1
        && barters[index]?.transactions.length
        && barters[index].transactions[barters[index].transactions.length - 1].type === TransactionType.Accepted;

    return <><div className='container-fluid'>
        {ready && <><div className='row'>
            <div className='offset-md-2 col-md-7'>
                {error && <div className='alert alert-danger alert-dismissible'>
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError('')} />
                </div>}
            </div>
        </div><div className='row'>
                <div className='col-md-2'></div>
                <div className='col-md-3'>
                    <ul className="list-group">
                        {barters.map(({ listing }, i) => <li key={listing._id} className={cn("list-group-item", i === index ? 'active' : '', '')} onClick={() => setIndex(i)}>
                            <div className='d-flex flex-wrap flex-nowrap'>
                                <div className='flex-grow-1'>
                                    <ImageBlur src={'/api/image?id=' + listing.images[0]} limitWidth={50} />
                                </div>
                                <div className='d-flex align-items-center'>
                                    <div className='px-2'>
                                        {listing.tagsAliases
                                            .map(alias => <span className='badge bg-secondary me-1' key={alias}>{tags.find(tag => tag.alias === alias)?.title}</span>)}
                                    </div>
                                </div>
                            </div>
                        </li>
                        )}
                    </ul>
                </div>
                <div className='col-md-4 mb-4'>
                    {currentBarterAgreed && <div className='alert alert-secondary'>This trade has concluded. Visit your <Link to='/orders'>orders</Link> page to view the order</div>}
                    {barters.map(({ listing, _id, outgoing, responseNeeded, buyerInventory, transactions, initialPrice }, i) => {
                        const lastPrice = transactions.length ? transactions[transactions.length - 1].priceCounter : initialPrice;
                        const lastItems = transactions.length ? transactions[transactions.length - 1].items : []
                        return <ol key={_id} className={cn('chat-list', i === index ? '' : 'd-none')}>

                            <br />
                            {!transactions.length && outgoing && <ChatBubble isSender={true}>
                                This item was listed at ${initialPrice} what would you like to do?
                            </ChatBubble>}
                            {transactions.map((transaction, i, transactions) => {
                                return <ChatBubble key={i} isSender={outgoing && !transaction.owner || !outgoing && transaction.owner}>
                                    {transaction.type === TransactionType.Accepted && <div>
                                        {agreeText(outgoing, transaction.owner)}
                                    </div>}
                                    {transaction.type === TransactionType.CounterOffer && <div>
                                        {counterText(outgoing,
                                            transaction.owner,
                                            transaction.previousItems,
                                            transaction.items,
                                            transaction.previousPrice,
                                            transaction.priceCounter, buyerInventory)}
                                    </div>}
                                    {transaction.type === TransactionType.Declined && <div>
                                        {declineText(outgoing, transaction.owner)}
                                    </div>}

                                </ChatBubble>;
                            })}
                            {responseNeeded &&
                                <div className={outgoing ? 'd-flex justify-content-end' : ''}>
                                    <button className='btn btn-sm btn-success m-1' onClick={() => {
                                        api.acceptTrade(_id)
                                            .then(() => refreshAllBarters())
                                            .catch(err => {
                                                setError(err);
                                            });
                                    }}>Accept</button>

                                    <CounterOffer
                                        barterId={_id}
                                        lastPrice={lastPrice}
                                        lastItems={lastItems}
                                        buyerInventory={buyerInventory}
                                        onSubmit={(items?, price?) => submitBarter(_id, items, price)} />
                                    <button className='btn btn-sm btn-light m-1' onClick={() => {
                                        api.declineTrade(_id)
                                            .then(() => refreshAllBarters())
                                            .catch(err => {
                                                setError(err);
                                            });
                                    }}>Cancel</button>
                                </div>}
                        </ol>;
                    })}
                </div>
                <div className='col-md-3'>

                </div>
            </div></>}
        {!ready && <div className="d-flex justify-content-center">
            {/*<FontAwesomeIcon icon='spinner' spin={true} />*/}
        </div>}
    </div></>
}

const CounterOffer: FunctionComponent<{
    barterId: string,
    lastPrice: number,
    lastItems: Array<string>,
    buyerInventory: Array<WithStringId<Listing>>,
    onSubmit: (items?: Array<string>, price?: number) => Promise<void>
}> = ({ lastItems, lastPrice, buyerInventory, onSubmit }) => {
    const [currentItems, setCurrentItems] = useState(lastItems);
    const [currentPrice, setCurrentPrice] = useState(lastPrice);
    const [currentPriceString, setCurrentPriceString] = useState(lastPrice + '');

    const btn = <button className='btn btn-sm btn-warning m-1'>
        Counter offer
    </button>

    return <FloatingCard bottom={true} button={btn} width={400}>
        <strong>Select items: </strong>
        <div className='row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-6 gx-3 gy-3 my-2'>
            {buyerInventory.map(listing => <div className='col'><img
                src={'/api/image?id=' + listing.images[0]}
                className={classNames('product-image', currentItems.findIndex(id => listing._id === id) !== -1 ? 'selected' : '', 'rounded')}
                onClick={() => currentItems.findIndex(id => listing._id === id) !== -1
                    ? setCurrentItems(currentItems.filter(item => item !== listing._id))
                    : setCurrentItems([...currentItems, listing._id])}
            /></div>)}
        </div>
        <strong>Price</strong>
        <div className='my-2'>
            <input className='form-control form-control-sm' value={'$ ' + currentPriceString} onChange={e => {
                const input = e.target.value;
                const priceString = input.replace(/[^0-9.]/g, "")
                const price = parseFloat(priceString || '0');
                setCurrentPriceString(priceString);
                setCurrentPrice(price);
            }} />
        </div>
        <div className='d-flex justify-content-end'>
            <button className='btn btn-primary btn-sm' onClick={() => {
                onSubmit(currentItems.length ? currentItems : undefined, lastPrice !== currentPrice ? currentPrice : undefined)
                    .then(() => { })

            }}>Submit</button>
        </div>

    </FloatingCard>
}
const ChatBubble: FunctionComponent<{ isSender: boolean }> = ({ isSender, children }) => {
    return (
        <li className={cn('shared', isSender ? 'sent' : 'received')}>
            {children}
        </li>
    )
}